import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { TrackingContextFields } from "../../models/MatchAdSearchResponse.js";
import { Recommendation, RecommendationResponse, SearchParams } from "../../types.js";
import { retryFetch } from "../utils/retryFetch.js";

type RecommendationsContextType = {
	items: Recommendation[];
	fetchMore: () => Promise<void>;
	fetchingInitial: boolean;
	fetchingMore: boolean;
	canFetchMore: boolean;
};

const RecommendationsContext = createContext<RecommendationsContextType>({
	items: [],
	fetchingInitial: true,
	fetchingMore: false,
	fetchMore: async () => {},
	canFetchMore: false,
});

export const useRecommendations = () => {
	return useContext(RecommendationsContext);
};

export const RecommendationsProvider = ({
	parameters,
	proxyUrl,
	children,
}: {
	parameters: SearchParams;
	proxyUrl: string;
	children: ReactNode;
}) => {
	const [fetchingInitial, setFetchingInitial] = useState(true);
	const [fetchingMore, setFetchingMore] = useState(false);
	const [recommendations, setRecommendations] = useState<{ items: Recommendation[]; fetchMore: string[] | undefined }>({
		items: [],
		fetchMore: undefined,
	});
	const [canFetchMore, setCanLoadMore] = useState(false);

	const fetchMore = async () => {
		const { fetchMore } = recommendations;
		if (fetchMore && fetchMore.length > 0) {
			const [item, ...rest] = fetchMore;
			setFetchingMore(true);
			const response = await fetchMoreRecommendations({
				proxyUrl,
				fetchMore: item,
				parameters,
			});
			setRecommendations({
				items: [...recommendations.items, ...response.items],
				fetchMore: rest,
			});
			setFetchingMore(false);
			setCanLoadMore(rest.length > 0);
		}
	};

	useEffect(() => {
		const initialFetch = async () => {
			if (proxyUrl) {
				const response = await fetchRecommendations({
					proxyUrl,
					searchParams: parameters,
				});
				setRecommendations(response);
				setCanLoadMore(!!response.fetchMore && response.fetchMore.length > 0);
				setFetchingInitial(false);
			}
		};
		initialFetch();
	}, []);

	return (
		<RecommendationsContext.Provider
			value={{ items: recommendations.items, fetchMore, fetchingInitial, fetchingMore, canFetchMore: canFetchMore }}
		>
			{children}
		</RecommendationsContext.Provider>
	);
};

const fetchRecommendations = async ({
	searchParams,
	proxyUrl,
	rows,
	render,
}: {
	searchParams: SearchParams;
	proxyUrl: string;
	rows?: string;
	render?: string;
}): Promise<RecommendationResponse> => {
	const parameters = new URLSearchParams(searchParams);
	if (rows) parameters.append("rows", rows);
	if (render) parameters.append("render", render);

	const url = `${proxyUrl}/recommendations?${parameters.toString()}`;

	try {
		const response = await retryFetch(url);
		const data: RecommendationResponse = await response.json();
		return mapResponse(data);
	} catch (e) {
		console.error(e);
		return {
			items: [],
			"tracking-context": {},
			fetchMore: undefined,
		};
	}
};

const fetchMoreRecommendations = async ({
	proxyUrl,
	fetchMore,
	parameters,
}: {
	proxyUrl: string;
	fetchMore: string;
	parameters: SearchParams;
}): Promise<RecommendationResponse> => {
	const searchParams = new URLSearchParams({ fetchMore, ...parameters });

	const url = `${proxyUrl}/more-recommendations?${searchParams.toString()}`;

	try {
		const response = await retryFetch(url);
		const data: RecommendationResponse = await response.json();
		return mapResponse(data);
	} catch (e) {
		console.error(e);
		return {
			items: [],
			"tracking-context": {},
			fetchMore: undefined,
		};
	}
};

const mapResponse = (data: RecommendationResponse): RecommendationResponse => {
	const trackingContexts = Object.entries(data["tracking-context"] ?? {});

	const recommendations: RecommendationResponse = {
		items: data.items.map((item) => {
			return {
				...item,
				tracking: {
					...item.tracking,
					pulse: item.tracking?.pulse
						? {
								...item.tracking.pulse,
								recommendation: item.tracking.pulse?.recommendation.map((event) => ({
									...event,
									...trackingContexts.reduce(
										(acc, [key, value]) => {
											acc[key] = value;
											return acc;
										},
										{} as Record<string, TrackingContextFields>,
									),
								})),
							}
						: undefined,
				},
			};
		}),
		"tracking-context": data["tracking-context"],
		fetchMore: data.fetchMore,
	};
	return recommendations;
};
